import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows, updateRow } from "@/lib/adminApi";
import {
  SETTLEMENT_STATUS,
  metaFor,
  optionsFrom,
  type FarmerSettlement,
} from "@/lib/adminModules";
import {
  DataTable,
  Drawer,
  DetailList,
  EmptyState,
  ErrorBanner,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnGhost,
  btnPrimary,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatRupees, formatDate } from "@/pages/admin/format";
import { exportToCsv, exportToPdf } from "@/pages/admin/exportUtils";
import { useAdminToast } from "@/hooks/useAdminToast";

export default function AdminSettlements() {
  const [rows, setRows] = useState<FarmerSettlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<FarmerSettlement>("farmer_settlements", "created_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load settlements.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const advance = async (row: FarmerSettlement) => {
    const next = row.status === "pending" ? "processing" : row.status === "processing" || row.status === "ready" ? "paid" : "paid";
    setRows((list) => list.map((item) => (item.id === row.id ? { ...item, status: next } : item)));
    try {
      await updateRow("farmer_settlements", row.id, { status: next });
      notify(next === "paid" ? `${row.farmer_name} marked as paid.` : `${row.farmer_name} moved to processing.`);
    } catch {
      setError("We couldn't update that settlement. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.farmer_name, row.farm_name || "", row.period || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => {
          const value = Number(row.final_settlement || 0);
          if (row.status === "paid") acc.paid += value;
          else acc.outstanding += value;
          acc.gross += Number(row.gross_booking || 0);
          return acc;
        },
        { gross: 0, paid: 0, outstanding: 0 },
      ),
    [rows],
  );

  const columns: Column<FarmerSettlement>[] = [
    {
      key: "farmer",
      header: "Farmer",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.farmer_name}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.farm_name || row.period || "—"}</p>
        </div>
      ),
    },
    { key: "period", header: "Period", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.period || "—"}</span> },
    { key: "gross", header: "Gross", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{formatRupees(row.gross_booking)}</span> },
    { key: "commission", header: "Commission", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{formatRupees(row.commission)}</span> },
    { key: "net", header: "Net payout", render: (row) => <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.final_settlement)}</span> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(SETTLEMENT_STATUS, row.status)} /> },
  ];

  const exportColumns = [
    { header: "Farmer", value: (row: FarmerSettlement) => row.farmer_name },
    { header: "Farm", value: (row: FarmerSettlement) => row.farm_name || "" },
    { header: "Period", value: (row: FarmerSettlement) => row.period || "" },
    { header: "Gross", value: (row: FarmerSettlement) => `${row.gross_booking}` },
    { header: "Commission", value: (row: FarmerSettlement) => `${row.commission}` },
    { header: "Net payout", value: (row: FarmerSettlement) => `${row.final_settlement}` },
    { header: "Status", value: (row: FarmerSettlement) => row.status },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Commerce"
        title="Settlements"
        subtitle="Payout cycles to farmers — what is owed, what is processing and what has been paid out, ready to reconcile."
        actions={
          <>
            <button type="button" className={btnGhost} onClick={() => exportToCsv("rythugaru-settlements", exportColumns, filtered)}>
              <i className="ri-file-excel-2-line"></i> CSV
            </button>
            <button type="button" className={btnPrimary} onClick={() => exportToPdf({ title: "Settlement Register", subtitle: "Farmer payouts", columns: exportColumns, rows: filtered })}>
              <i className="ri-file-pdf-2-line"></i> PDF
            </button>
          </>
        }
      />

      <StatCards
        stats={[
          { label: "Gross booked", value: formatRupees(totals.gross), icon: "ri-money-rupee-circle-line", tone: "primary" },
          { label: "Paid out", value: formatRupees(totals.paid), icon: "ri-checkbox-circle-line", tone: "primary" },
          { label: "Outstanding", value: formatRupees(totals.outstanding), icon: "ri-time-line", tone: "accent" },
          { label: "Settlements", value: rows.length, icon: "ri-exchange-dollar-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by farmer, farm, period…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...optionsFrom(SETTLEMENT_STATUS)]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} settlements</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading settlements…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-exchange-dollar-line" title={rows.length === 0 ? "No settlements yet" : "No settlements match"} message="Farmer payouts will appear here once bookings are completed." />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.farmer_name || "Settlement"}
        subtitle="Settlement detail"
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              {selected.status !== "paid" ? (
                <button type="button" onClick={() => advance(selected)} className={btnPrimary}>
                  <i className="ri-check-double-line"></i>
                  {selected.status === "pending" ? "Start processing" : "Mark as paid"}
                </button>
              ) : (
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700"><i className="ri-checkbox-circle-fill"></i> Settlement complete</span>
              )}
              <button type="button" onClick={() => setSelectedId(null)} className={btnGhost}>Close</button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(SETTLEMENT_STATUS, selected.status)} />
              <span className="text-xs font-semibold text-foreground-800 bg-background-100 border border-background-300 px-2.5 py-1 rounded-full">{selected.period || "Period"}</span>
            </div>

            <DetailList
              items={[
                { icon: "ri-plant-line", label: "Farm", value: selected.farm_name || "—" },
                { icon: "ri-money-rupee-circle-line", label: "Gross booking", value: formatRupees(selected.gross_booking) },
                { icon: "ri-percent-line", label: "Commission", value: formatRupees(selected.commission) },
                { icon: "ri-tools-line", label: "Operational costs", value: formatRupees(selected.operational_costs) },
                { icon: "ri-refund-2-line", label: "Refund adjustments", value: formatRupees(selected.refund_adjustments) },
                { icon: "ri-hand-coin-line", label: "Farmer share", value: formatRupees(selected.farmer_share) },
                { icon: "ri-exchange-dollar-line", label: "Final settlement", value: formatRupees(selected.final_settlement) },
                { icon: "ri-calendar-line", label: "Created", value: formatDate(selected.created_at) },
              ]}
            />
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Approving here updates the payout status for everyone.
      </p>
      {node}
    </div>
  );
}